import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { ClipboardDocumentCheckIcon } from '@heroicons/react/24/solid';
import React, { useState } from 'react';

import DatevBookingDataDisplay from '../../../components/DatevBookingDataDisplay';
import Filename from '../../../components/Filename';
import SplitPanelBookingLayout from '../../../components/invoice-preview/SplitPanelBookingLayout';
import { UploadedReceipt, DatevBookingData } from '../../../types';

interface DatevBookingModalProps {
  receipt: UploadedReceipt;
  bookingData: DatevBookingData[];
  originalBookingData: DatevBookingData[] | undefined;
  onSave?: (data: DatevBookingData[]) => void;
  onRestore?: () => void;
  onClose: () => void;
}

const DatevBookingModal: React.FC<DatevBookingModalProps> = ({
  receipt,
  bookingData,
  originalBookingData,
  onSave,
  onRestore,
  onClose,
}) => {
  const isImported = receipt.status === 'imported';
  const receiptId = receipt.id;
  const originalFileName = receipt.originalFileName;
  const storageKey = receipt.storageKey;
  const [copiedField, setCopiedField] = useState<'name' | 'id' | null>(null);

  const handleCopy = (text: string, field: 'name' | 'id') => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500);
  };

  return (
    <div
      className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'
      role='presentation'
    >
      <button
        className='absolute inset-0 w-full h-full cursor-default'
        onClick={onClose}
        aria-label='Close modal'
        tabIndex={-1}
      />
      <div
        className='bg-white rounded-lg p-6 w-[85%] max-h-[90vh] overflow-y-auto relative z-10'
        role='dialog'
        aria-modal='true'
      >
        <div className='flex justify-between items-center'>
          <div className='flex items-center gap-2'>
            <h3 className='text-lg font-semibold text-gray-900'>
              DATEV Booking Data ({bookingData.length}{' '}
              {bookingData.length === 1 ? 'entry' : 'entries'})
            </h3>
            <span className='text-sm text-gray-500'>
              (<Filename
                storageKey={storageKey}
                fileName={originalFileName}
              />)
            </span>
            <button
              type='button'
              onClick={() => handleCopy(originalFileName, 'name')}
              className='p-0.5 text-gray-400 hover:text-gray-600 rounded transition-colors'
              title='Copy file name'
            >
              {copiedField === 'name' ? (
                <ClipboardDocumentCheckIcon className='w-3.5 h-3.5 text-green-500' />
              ) : (
                <ClipboardDocumentIcon className='w-3.5 h-3.5' />
              )}
            </button>
          </div>
          <button
            onClick={onClose}
            className='text-gray-500 hover:text-gray-700 text-2xl ml-auto'
          >
            &times;
          </button>
        </div>
        <div className='flex items-center gap-1 mb-4'>
          <span
            className='text-xs italic text-gray-400 font-mono'
            title={receiptId}
          >
            {receiptId}
          </span>
          <button
            type='button'
            onClick={() => handleCopy(receiptId, 'id')}
            className='p-0.5 text-gray-400 hover:text-gray-600 rounded transition-colors'
            title='Copy receipt ID'
          >
            {copiedField === 'id' ? (
              <ClipboardDocumentCheckIcon className='w-3 h-3 text-green-500' />
            ) : (
              <ClipboardDocumentIcon className='w-3 h-3' />
            )}
          </button>
        </div>
        <SplitPanelBookingLayout receipt={receipt}>
          <DatevBookingDataDisplay
            bookingData={bookingData}
            originalBookingData={originalBookingData}
            onSave={!isImported ? onSave : undefined}
            onRestore={!isImported ? onRestore : undefined}
            showHeader={false}
          />
        </SplitPanelBookingLayout>
      </div>
    </div>
  );
};

export default DatevBookingModal;
